import { Cell, CrystalKind, DIRS, Dir, Enemy, EnemyKind, GameEvent, GameState, Mover, Particle, PowerKind, Slider, Snapshot } from "./types";
import { Dims, LevelData, MAX_LEVEL, MOBILE_DIMS, generateLevel, generateTunnel, inBounds, isBonusAfter } from "./level";
import { applySetback, loadBest, saveBest, saveLevelBest, saveUnlocked } from "./storage";
import { t } from "./i18n";

const PLAYER_MS = 175;
const SLIDE_MS = 60;
const DEATH_MS = 1100;
const POWER_MS = 6000;
const POWER_TTL = 12000;
const SPAWN_MS = 1200;
const TUNNEL_MS = 60000;
const WAVE_MS = 30000;
const FADE_MS = 8000;
const DAILY_BASE_LEVEL = 6;
export const BREATHER_MS = 6500;
const TUNNEL_CRUSH_BONUS = 250;
const SCORE = { scorpion: 200, spider: 300, snake: 350, bat: 500, bouncer: 400, shatter: 10, level: 1000, powerup: 100, eat: 300, explode: 300, tunnel: 2000 };

export const lerpPos = (m: Mover) => ({ x: m.x + (m.tx - m.x) * m.t, y: m.y + (m.ty - m.y) * m.t });

export class Game {
  state: GameState;
  dims: Dims;
  dimsFn: (() => Dims) | null = null;
  best = loadBest();
  god = false;
  private ballTimer = 0;
  private dailyKey: string | null = null;
  held: Dir | null = null;
  private trailT = 0;
  swipe: Dir | null = null;
  pushRequested = false;
  onEvent: (e: GameEvent) => void = () => {};
  private listeners = new Set<(s: Snapshot) => void>();
  private nextId = 1;
  private bfs: Int16Array;

  constructor(dims: Dims = MOBILE_DIMS) {
    this.dims = dims;
    this.bfs = new Int16Array(dims.cols * dims.rows);
    this.state = this.freshState(1);
    this.state.phase = "start";
  }

  subscribe(fn: (s: Snapshot) => void) {
    this.listeners.add(fn);
    fn(this.snapshot());
    return () => { this.listeners.delete(fn); };
  }

  snapshot(): Snapshot {
    const s = this.state;
    return { phase: s.phase, score: s.score, lives: s.lives, level: s.level, retryAt: s.retryAt, best: this.best, enemiesLeft: s.enemies.length + s.spawns.length + s.reserve.length, letters: s.letters, god: this.god, bonus: s.bonus, bonusResult: s.bonusResult, timedOut: s.bonus && s.timeLeft <= 0, daily: s.daily, wave: s.wave, elapsed: s.elapsed, breather: s.breather, introHold: s.introHold };
  }

  private speedLevel() { const s = this.state; return s?.daily ? 4 + s.wave * 4 : s?.level ?? 1; }

  toggleGod() { this.god = !this.god; this.notify(); }

  private notify() {
    const snap = this.snapshot();
    this.listeners.forEach((fn) => fn(snap));
  }

  private speedOf(kind: EnemyKind, level: number, small = this.state?.cols * this.state?.rows <= 81) {
    return Math.round(this.baseSpeedOf(kind, level) * (small ? 1.18 : 1));
  }

  private baseSpeedOf(kind: EnemyKind, level: number) {
    switch (kind) {
      case "snake": return Math.max(350, 460 - level * 3);
      case "spider": return Math.max(360, 500 - level * 4);
      case "bat": return Math.max(300, 400 - level * 3);
      case "bouncer": return Math.max(250, 300 - level * 2);
      default: return Math.max(400, 560 - level * 4);
    }
  }

  private chaseOf(kind: EnemyKind, level: number) {
    const k = Math.min(1, Math.max(0, level - 1) / 45);
    switch (kind) {
      case "snake": return 0.45 + 0.35 * k;
      case "spider": return 0.25 + 0.3 * k;
      default: return 0.12 + 0.23 * k;
    }
  }

  private makeEnemy(x: number, y: number, kind: EnemyKind, wait: number): Enemy {
    return {
      id: this.nextId++, x, y, tx: x, ty: y, t: 1, dir: "down", kind,
      speed: this.speedOf(kind, this.speedLevel()), wait, stuck: 0,
      flying: kind === "bat", frozen: 0, flyTimer: 2500 + Math.random() * 2500,
    };
  }

  private baseState(level: number, data: LevelData, score: number, lives: number, letters: boolean[]): GameState {
    this.bfs = new Int16Array(data.grid[0].length * data.grid.length);
    return {
      cols: data.grid[0].length, rows: data.grid.length,
      grid: data.grid,
      kinds: data.kinds,
      ballCap: data.kinds.flat().filter((k) => k === "ball").length,
      player: { x: data.player.x, y: data.player.y, tx: data.player.x, ty: data.player.y, t: 1, dir: "up", invuln: 1500, deadTimer: 0 },
      enemies: data.enemies.map((e) => ({ ...this.makeEnemy(e.x, e.y, e.kind, 600 + Math.random() * 600), speed: this.speedOf(e.kind, level, data.grid[0].length * data.grid.length <= 81) })),
      reserve: data.reserve, spawns: [], cap: data.cap,
      bonus: false, bonusResult: null, exit: null, timeLeft: 0, timeMax: 0,
      sliders: [], particles: [], powerups: [], popups: [], flashes: [], bombs: [], bubbles: [], letters: [...letters], letterTimer: 6000 + Math.random() * 4000,
      power: 0, diamondsArmed: true, bonesUsed: false, breather: false, daily: false, ballFloor: 0, retryAt: 0, wave: 0, waveTimer: 0, elapsed: 0, fading: [], freeze: 0, introHold: 2000, ghost: 0, spawnTimer: 7000 + Math.random() * 5000,
      score, levelStartScore: score, lives, level, phase: "playing", shake: 0, lastCombo: 0,
    };
  }

  private freshState(level: number, score = 0, lives = 3, letters: boolean[] = [false, false, false, false, false]): GameState {
    if (this.dimsFn) this.dims = this.dimsFn();
    const st = this.baseState(level, generateLevel(level, this.dims), score, lives, letters);
    st.player.dir = "down";
    return st;
  }

  private tunnelState(afterLevel: number, score: number, lives: number, letters: boolean[]): GameState {
    const data = generateTunnel(afterLevel);
    const st = this.baseState(afterLevel, data, score, lives, letters);
    st.bonus = true;
    st.exit = data.exit;
    st.introHold = 2600;
    st.timeMax = st.timeLeft = TUNNEL_MS;
    return st;
  }

  start() { this.dailyKey = null; this.startAt(1); }

  startDaily(dayKey: string) {
    this.dailyKey = dayKey;
    if (this.dimsFn) this.dims = this.dimsFn();
    let h = 2166136261;
    for (const ch of dayKey) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619) >>> 0; }
    const data = generateLevel(DAILY_BASE_LEVEL, this.dims, (h % 1_000_000) + 7);
    const st = this.baseState(DAILY_BASE_LEVEL, data, 0, 3, [false, false, false, false, false]);
    st.player.dir = "down";
    st.daily = true; st.wave = 1; st.waveTimer = WAVE_MS; st.cap = 4;
    st.ballFloor = Math.round(st.kinds.flat().filter((k) => k === "ball").length * 0.7);
    this.state = st;
    this.held = null; this.swipe = null; this.pushRequested = false;
    this.onEvent("start");
    this.notify();
  }

  private nextWave() {
    const s = this.state;
    s.wave++;
    s.waveTimer = WAVE_MS;
    s.cap = Math.min(8, 3 + s.wave);
    s.score += 300;
    const pool: EnemyKind[] = ["scorpion", "scorpion", "spider"];
    if (s.wave >= 2) pool.push("snake");
    if (s.wave >= 3) pool.push("spider", "snake");
    if (s.wave >= 4) pool.push("bat");
    if (s.wave >= 6) pool.push("bouncer");
    const n = Math.min(6, 2 + Math.floor(s.wave / 2));
    for (let i = 0; i < n; i++) s.reserve.push(pool[Math.floor(Math.random() * pool.length)]);
    for (const e of s.enemies) e.speed = this.speedOf(e.kind, this.speedLevel());
    s.popups.push({ x: s.cols / 2, y: s.rows / 2, text: t("waveN", { n: s.wave }), life: 1800, max: 1800 });
    s.shake = Math.max(s.shake, 5);
    this.onEvent("wave");
    this.notify();
  }

  private isEdge(x: number, y: number) { const s = this.state; return x === 0 || y === 0 || x === s.cols - 1 || y === s.rows - 1; }

  private updateEdgeFade(dt: number) {
    const s = this.state;
    const fadeKind = (k: string | null) => k === "ball" || (!s.daily && (k === "diamond" || k === "diamond2"));
    const isEdgeBall = (x: number, y: number) => this.isEdge(x, y) && s.grid[y][x] === Cell.Crystal && fadeKind(s.kinds[y][x]);
    for (let i = s.fading.length - 1; i >= 0; i--) {
      const f = s.fading[i];
      if (!isEdgeBall(f.x, f.y)) { s.fading.splice(i, 1); continue; }
      f.t += dt;
      if (f.t >= FADE_MS) {
        const kind = s.kinds[f.y][f.x];
        s.grid[f.y][f.x] = Cell.Floor; s.kinds[f.y][f.x] = null;
        this.burst(f.x + 0.5, f.y + 0.5, kind === "ball" ? "#e2e8f0" : "#fde68a", 6);
        s.fading.splice(i, 1);
      }
    }
    for (let y = 0; y < s.rows; y++) for (let x = 0; x < s.cols; x++) {
      if (!isEdgeBall(x, y)) continue;
      if (!s.fading.some((f) => f.x === x && f.y === y)) s.fading.push({ x, y, t: 0 });
    }
  }

  startAt(level: number) {
    this.dailyKey = null;
    this.state = this.freshState(level);
    this.held = null; this.swipe = null; this.pushRequested = false;
    this.onEvent("start");
    this.notify();
  }

  restart() { if (this.dailyKey) this.startDaily(this.dailyKey); else this.start(); }
  retryLevel() { return this.state.retryAt || this.state.level; }

  quit() {
    this.dailyKey = null;
    this.state = this.freshState(1);
    this.state.phase = "start";
    this.held = null; this.swipe = null; this.pushRequested = false;
    this.notify();
  }

  transposeForOrientation(landscape: boolean) {
    const s = this.state;
    if (s.bonus || s.phase === "start" || s.cols === s.rows) return;
    if ((landscape && s.cols > s.rows) || (!landscape && s.rows > s.cols)) return;
    const flipDir = (d: Dir): Dir => (d === "up" ? "left" : d === "left" ? "up" : d === "down" ? "right" : "down");
    const swap = <T extends { x: number; y: number }>(o: T) => { const x = o.x; o.x = o.y; o.y = x; };
    const swapMover = (m: Mover) => { swap(m); const tx = m.tx; m.tx = m.ty; m.ty = tx; m.dir = flipDir(m.dir); };
    const cols = s.rows, rows = s.cols;
    s.grid = Array.from({ length: rows }, (_, y) => Array.from({ length: cols }, (_, x) => s.grid[x][y]));
    s.kinds = Array.from({ length: rows }, (_, y) => Array.from({ length: cols }, (_, x) => s.kinds[x][y]));
    s.cols = cols; s.rows = rows;
    swapMover(s.player);
    s.enemies.forEach(swapMover);
    s.sliders.forEach(swapMover);
    s.powerups.forEach(swap); s.bubbles.forEach(swap); s.spawns.forEach(swap); s.bombs.forEach(swap); s.popups.forEach(swap); s.flashes.forEach(swap);
    s.particles.forEach((p) => { swap(p); const vx = p.vx; p.vx = p.vy; p.vy = vx; });
    s.fading.forEach(swap);
    this.dims = { cols, rows };
    this.bfs = new Int16Array(cols * rows);
    this.held = null; this.swipe = null;
    this.notify();
  }

  nextLevel() {
    const { level, score, lives, letters, bonus, bonusResult } = this.state;
    if (!bonus && isBonusAfter(level)) {
      this.state = this.tunnelState(level, score, lives, letters);
      this.swipe = null;
      this.onEvent("tunnel");
      this.notify();
      return;
    }
    const target = Math.min(MAX_LEVEL, level + (bonus && bonusResult === "win" ? 2 : 1));
    saveUnlocked(target);
    this.state = this.freshState(target, score, lives, letters);
    if (bonus) { this.state.breather = true; this.state.introHold = BREATHER_MS; }
    this.swipe = null;
    this.onEvent("start");
    this.notify();
  }

  togglePause() {
    if (this.state.phase === "playing") this.state.phase = "paused";
    else if (this.state.phase === "paused") this.state.phase = "playing";
    else return;
    this.notify();
  }

  setHeld(d: Dir | null) { this.held = d; if (d) this.swipe = null; }
  setSwipe(d: Dir | null) { this.swipe = d; }
  pushAction() { this.pushRequested = true; }

  update(dt: number) {
    const s = this.state;
    s.shake = Math.max(0, s.shake - dt * 0.03);
    this.updateParticles(dt);
    for (let i = s.popups.length - 1; i >= 0; i--) { s.popups[i].life -= dt; if (s.popups[i].life <= 0) s.popups.splice(i, 1); }
    for (let i = s.flashes.length - 1; i >= 0; i--) { s.flashes[i].life -= dt; if (s.flashes[i].life <= 0.5) s.flashes.splice(i, 1); }
    if (s.phase !== "playing") return;

    if (s.player.deadTimer > 0) {
      s.player.deadTimer -= dt;
      if (s.player.deadTimer <= 0) this.afterDeath();
      return;
    }

    this.updatePlayer(dt);
    if (!s.bonus) {
      this.updatePowerups(dt);
      if (!s.daily) this.updateLetters(dt); else s.power = Math.max(0, s.power - dt);
    }
    this.updateBombs(dt);
    s.introHold = Math.max(0, s.introHold - dt);
    if (s.breather && s.introHold <= 0) s.breather = false;
    this.updateSliders(dt);
    if (!s.bonus && s.phase === "playing") this.updateEdgeFade(dt);
    this.ensureCrystals(dt);
    this.ensureBones();
    if (!s.bonus && s.reserve.length && s.enemies.length + s.spawns.length < s.cap) this.scheduleReinforcements();
    this.updateSpawns(dt);
    this.updateEnemies(dt);
    this.checkPlayerHit();

    if (s.daily) {
      if (s.phase !== "playing") return;
      if (s.introHold > 0) return;
      s.elapsed += dt;
      s.waveTimer -= dt;
      if (s.waveTimer <= 0 || (s.enemies.length === 0 && s.spawns.length === 0 && s.reserve.length === 0)) this.nextWave();
      return;
    }

    if (s.bonus) {
      const p = s.player;
      if (s.introHold <= 0 && s.phase === "playing") {
        const before = Math.ceil(s.timeLeft / 1000);
        s.timeLeft = Math.max(0, s.timeLeft - dt);
        const after = Math.ceil(s.timeLeft / 1000);
        if (after < before && after <= 10 && after > 0) this.onEvent("tick");
        if (s.timeLeft <= 0) {
          s.bonusResult = "fail";
          s.phase = "levelclear";
          s.shake = 10;
          this.onEvent("timeout");
          this.notify();
          return;
        }
      }
      if (s.exit && p.t >= 1 && p.y === s.exit.y && s.phase === "playing") {
        s.score += SCORE.tunnel + Math.floor(s.timeLeft / 1000) * 20;
        s.bonusResult = "win";
        s.phase = "levelclear";
        this.saveHigh();
        this.onEvent("exit");
        this.notify();
      }
      return;
    }

    if (s.enemies.length === 0 && s.spawns.length === 0 && s.reserve.length === 0 && s.phase === "playing") {
      s.score += SCORE.level;
      saveLevelBest(s.level, s.score - s.levelStartScore);
      this.saveHigh();
      if (s.level >= MAX_LEVEL) {
        s.phase = "ending";
        this.onEvent("ending");
      } else {
        s.phase = "levelclear";
        saveUnlocked(s.level + 1);
        this.onEvent("levelclear");
      }
      this.notify();
    }
  }

  private scheduleReinforcements() {
    const s = this.state;
    while (s.reserve.length && s.enemies.length + s.spawns.length < s.cap && s.spawns.length < 2) {
      const spot = this.randomFreeSpot(5);
      if (!spot) return;
      s.spawns.push({ ...spot, kind: s.reserve.shift()!, timer: SPAWN_MS, max: SPAWN_MS });
      this.onEvent("spawn");
    }
  }

  private updateSpawns(dt: number) {
    const s = this.state;
    for (const sp of [...s.spawns]) {
      sp.timer -= dt;
      if (sp.timer > 0) continue;
      s.spawns.splice(s.spawns.indexOf(sp), 1);
      if (!this.isFree(sp.x, sp.y) || this.enemyAt(sp.x, sp.y)) {
        const alt = this.randomFreeSpot(4);
        if (!alt) { s.reserve.unshift(sp.kind); continue; }
        sp.x = alt.x; sp.y = alt.y;
      }
      s.enemies.push(this.makeEnemy(sp.x, sp.y, sp.kind, 700));
      this.burst(sp.x + 0.5, sp.y + 0.5, "#fb7185", 14);
      s.flashes.push({ x: sp.x + 0.5, y: sp.y + 0.5, life: 400, max: 400, color: "#fb7185", radius: 0.9 });
      this.notify();
    }
  }

  private isFree(x: number, y: number, ignoreSlider?: Slider) {
    const s = this.state;
    if (!inBounds(x, y, s.cols, s.rows) || s.grid[y][x] !== Cell.Floor) return false;
    return !s.sliders.some((sl) => sl !== ignoreSlider && ((sl.x === x && sl.y === y) || (sl.tx === x && sl.ty === y)));
  }

  private updateLetters(dt: number) {
    const s = this.state;
    const p = s.player;
    s.power = Math.max(0, s.power - dt);
    for (const b of [...s.bubbles]) {
      b.ttl -= dt;
      if (b.ttl <= 0) { s.bubbles.splice(s.bubbles.indexOf(b), 1); continue; }
      if (p.t >= 1 && p.x === b.x && p.y === b.y) this.collectLetter(b);
    }
    s.letterTimer -= dt;
    if (s.letterTimer <= 0) {
      s.letterTimer = 9000 + Math.random() * 6000;
      const missing = s.letters.map((v, i) => (v ? -1 : i)).filter((i) => i >= 0);
      if (s.bubbles.length === 0 && missing.length && Math.random() < 0.7) {
        const spot = this.randomFreeSpot();
        if (spot) s.bubbles.push({ ...spot, idx: missing[Math.floor(Math.random() * missing.length)], ttl: 14000 });
      }
    }
  }

  private collectLetter(b: GameState["bubbles"][number]) {
    const s = this.state;
    s.bubbles.splice(s.bubbles.indexOf(b), 1);
    s.letters[b.idx] = true;
    s.score += 150;
    this.burst(b.x + 0.5, b.y + 0.5, "#f9a8d4", 12);
    if (s.letters.every(Boolean)) {
      s.letters = [false, false, false, false, false];
      s.lives = Math.min(5, s.lives + 1);
      s.popups.push({ x: b.x + 0.5, y: b.y + 0.5, text: "PUPPY!", life: 1600, max: 1600 });
      this.onEvent("extra");
    } else {
      this.onEvent("letter");
    }
    this.notify();
  }

  private randomFreeSpot(minDist = 3, inner = false): { x: number; y: number } | null {
    const s = this.state;
    const p = s.player;
    const spots: { x: number; y: number }[] = [];
    for (let y = 0; y < s.rows; y++) for (let x = 0; x < s.cols; x++)
      if (this.isFree(x, y) && !this.enemyAt(x, y) && !(inner && this.isEdge(x, y)) && Math.abs(x - p.x) + Math.abs(y - p.y) >= minDist && !s.powerups.some((u) => u.x === x && u.y === y) && !s.bubbles.some((b) => b.x === x && b.y === y) && !s.spawns.some((sp) => sp.x === x && sp.y === y)) spots.push({ x, y });
    if (!spots.length && minDist > 2) return this.randomFreeSpot(minDist - 1, inner);
    if (!spots.length && inner) return this.randomFreeSpot(minDist, false);
    return spots.length ? spots[Math.floor(Math.random() * spots.length)] : null;
  }

  private isDiamond(x: number, y: number) {
    const s = this.state;
    return inBounds(x, y, s.cols, s.rows) && s.grid[y][x] === Cell.Crystal && (s.kinds[y][x] === "diamond" || s.kinds[y][x] === "diamond2");
  }

  private mergeDiamond(fromKind: CrystalKind, tx: number, ty: number) {
    const s = this.state;
    const total = (fromKind === "diamond2" ? 2 : 1) + (s.kinds[ty][tx] === "diamond2" ? 2 : 1);
    if (total < 3) {
      s.kinds[ty][tx] = "diamond2";
      this.burst(tx + 0.5, ty + 0.5, "#fde68a", 18);
      s.flashes.push({ x: tx + 0.5, y: ty + 0.5, life: 550, max: 550, color: "#fde047", radius: 1.1 });
      s.shake = Math.max(s.shake, 4);
      this.onEvent("merge");
      return;
    }
    s.grid[ty][tx] = Cell.Floor;
    s.kinds[ty][tx] = null;
    s.power = 9000;
    s.score += 500;
    s.flashes.push({ x: tx + 0.5, y: ty + 0.5, life: 900, max: 900, color: "#fbbf24", radius: 2.2 });
    s.popups.push({ x: tx + 0.5, y: ty + 0.5, text: "POWER!", life: 2000, max: 2000 });
    this.burst(tx + 0.5, ty + 0.5, "#fbbf24", 28);
    s.shake = 8;
    this.onEvent("power");
    s.bonesUsed = !s.daily;
    if (s.daily) {
      this.spawnBones(3);
      s.popups.push({ x: s.cols / 2, y: s.rows / 2 + 1.5, text: t("newBones"), life: 1500, max: 1500 });
    }
    this.notify();
  }

  private boneUnits() {
    const s = this.state;
    let n = 0;
    for (let y = 0; y < s.rows; y++) for (let x = 0; x < s.cols; x++) {
      if (s.grid[y][x] !== Cell.Crystal) continue;
      if (s.kinds[y][x] === "diamond") n += 1;
      else if (s.kinds[y][x] === "diamond2") n += 2;
    }
    for (const sl of s.sliders) n += sl.kind === "diamond" ? 1 : sl.kind === "diamond2" ? 2 : 0;
    return n;
  }

  private spawnBones(count: number) {
    const s = this.state;
    for (let i = 0; i < count; i++) {
      const sp = this.randomFreeSpot(3, true);
      if (!sp) break;
      s.grid[sp.y][sp.x] = Cell.Crystal;
      s.kinds[sp.y][sp.x] = "diamond";
      this.burst(sp.x + 0.5, sp.y + 0.5, "#fde68a", 8);
      s.flashes.push({ x: sp.x + 0.5, y: sp.y + 0.5, life: 700, max: 700, color: "#fde047", radius: 1.0 });
    }
  }

  private ensureBones() {
    const s = this.state;
    if (s.bonus || s.bonesUsed || s.phase !== "playing") return;
    const units = this.boneUnits();
    if (units < 3) this.spawnBones(3 - units);
  }

  private updatePowerups(dt: number) {
    const s = this.state;
    const p = s.player;
    s.freeze = Math.max(0, s.freeze - dt);
    s.ghost = Math.max(0, s.ghost - dt);
    for (const pu of [...s.powerups]) {
      pu.ttl -= dt;
      if (pu.ttl <= 0) { s.powerups.splice(s.powerups.indexOf(pu), 1); continue; }
      if (p.t >= 1 && p.x === pu.x && p.y === pu.y) this.collectPower(pu);
    }
    s.spawnTimer -= dt;
    if (s.spawnTimer <= 0) {
      s.spawnTimer = 9000 + Math.random() * 7000;
      if (s.powerups.length === 0 && Math.random() < 0.65) this.spawnPower();
    }
  }

  private spawnPower() {
    const s = this.state;
    const p = s.player;
    const spots: { x: number; y: number }[] = [];
    for (let y = 0; y < s.rows; y++) for (let x = 0; x < s.cols; x++)
      if (this.isFree(x, y) && !this.enemyAt(x, y) && Math.abs(x - p.x) + Math.abs(y - p.y) >= 3) spots.push({ x, y });
    if (!spots.length) return;
    const sp = spots[Math.floor(Math.random() * spots.length)];
    const kind: PowerKind = Math.random() < 0.5 ? "freeze" : "ghost";
    s.powerups.push({ ...sp, kind, ttl: POWER_TTL });
    this.burst(sp.x + 0.5, sp.y + 0.5, kind === "freeze" ? "#bae6fd" : "#fef3c7", 8);
  }

  private collectPower(pu: GameState["powerups"][number]) {
    const s = this.state;
    s.powerups.splice(s.powerups.indexOf(pu), 1);
    this.applyPower(pu.kind);
  }

  collectAt(x: number, y: number) {
    const s = this.state;
    const pu = s.powerups.find((u) => u.x === x && u.y === y);
    if (pu) this.collectPower(pu);
    const b = s.bubbles.find((u) => u.x === x && u.y === y);
    if (b) this.collectLetter(b);
  }

  private applyPower(kind: PowerKind) {
    const s = this.state;
    s.score += SCORE.powerup;
    if (kind === "freeze") {
      s.freeze = POWER_MS;
      for (const e of s.enemies) {
        if (e.kind !== "bat") continue;
        e.x = e.tx; e.y = e.ty; e.t = 1;
        if (s.grid[e.y][e.x] === Cell.Floor) { e.flying = false; e.flyTimer = POWER_MS + 500; }
      }
    }
    else { s.ghost = POWER_MS; s.player.invuln = Math.max(s.player.invuln, POWER_MS); }
    this.burst(s.player.x + 0.5, s.player.y + 0.5, kind === "freeze" ? "#7dd3fc" : "#fde68a", 16);
    this.onEvent("powerup");
    this.notify();
  }

  private enemyAt(x: number, y: number, except?: Enemy) {
    return this.state.enemies.some((e) => e !== except && ((e.x === x && e.y === y) || (e.tx === x && e.ty === y)));
  }

  private updatePlayer(dt: number) {
    const p = this.state.player;
    p.invuln = Math.max(0, p.invuln - dt);
    if (this.state.breather) { this.swipe = null; this.pushRequested = false; return; }
    if (p.t < 1) {
      const need = (1 - p.t) * PLAYER_MS;
      this.sparkle(p, Math.min(dt, need));
      if (dt < need) { p.t += dt / PLAYER_MS; return; }
      p.t = 1; p.x = p.tx; p.y = p.ty;
      this.collectAt(p.x, p.y);
      dt -= need;
    }
    const dir = this.held ?? this.swipe;
    if (this.pushRequested) {
      this.pushRequested = false;
      const f = DIRS[p.dir];
      const fx = p.x + f.dx, fy = p.y + f.dy;
      if (inBounds(fx, fy, this.state.cols, this.state.rows) && this.state.grid[fy][fx] === Cell.Crystal) { this.push(fx, fy, p.dir); return; }
    }
    if (!dir) return;
    p.dir = dir;
    const { dx, dy } = DIRS[dir];
    const nx = p.x + dx, ny = p.y + dy;
    if (!inBounds(nx, ny, this.state.cols, this.state.rows) || this.state.grid[ny][nx] === Cell.Wall) { this.swipe = null; return; }
    if (this.state.grid[ny][nx] === Cell.Crystal) { this.swipe = null; return; }
    if (this.state.sliders.some((sl) => (sl.tx === nx && sl.ty === ny) || (sl.x === nx && sl.y === ny))) { this.swipe = null; return; }
    p.tx = nx; p.ty = ny; p.t = Math.min(0.5, dt / PLAYER_MS);
  }

  private sparkle(p: GameState["player"], dt: number) {
    this.trailT += dt;
    if (this.trailT < 55 || this.state.player.deadTimer > 0) return;
    this.trailT = 0;
    const { dx, dy } = DIRS[p.dir];
    const pos = lerpPos(p);
    const x = pos.x + 0.5 - dx * 0.35 + (Math.random() - 0.5) * 0.3;
    const y = pos.y + 0.6 - dy * 0.35 + (Math.random() - 0.5) * 0.3;
    const gold = Math.random() < 0.6;
    this.state.particles.push({ x, y, vx: -dx * 1.2 + (Math.random() - 0.5), vy: -dy * 1.2 - 0.6 - Math.random() * 0.8, life: 320 + Math.random() * 160, max: 450, color: gold ? "#fde047" : "#ffffff", size: 0.1 + Math.random() * 0.1, star: true });
  }

  private push(cx: number, cy: number, dir: Dir) {
    const s = this.state;
    const { dx, dy } = DIRS[dir];
    const bx = cx + dx, by = cy + dy;
    const kind = s.kinds[cy][cx] ?? "ball";
    const isDia = kind === "diamond" || kind === "diamond2";
    if (isDia && this.isDiamond(bx, by) && !s.sliders.some((sl) => sl.tx === bx && sl.ty === by)) {
      s.grid[cy][cx] = Cell.Floor;
      s.kinds[cy][cx] = null;
      this.mergeDiamond(kind, bx, by);
      return;
    }
    if (!this.isFree(bx, by)) {
      if (isDia) { s.shake = Math.max(s.shake, 2); return; }
      s.grid[cy][cx] = Cell.Floor;
      s.kinds[cy][cx] = null;
      if (kind === "bomb") { s.grid[cy][cx] = Cell.Crystal; s.kinds[cy][cx] = "bomb"; this.armBomb(cx, cy); return; }
      s.score += SCORE.shatter;
      this.burst(cx + 0.5, cy + 0.5, "#ffffff", 14);
      s.shake = 4;
      this.onEvent("shatter");
      this.ensureCrystals();
      this.notify();
      return;
    }
    s.grid[cy][cx] = Cell.Floor;
    s.kinds[cy][cx] = null;
    s.sliders.push({ x: cx, y: cy, tx: bx, ty: by, t: 0, dir, kind, crushed: 0, bounces: kind === "bounce1" ? 1 : kind === "bounce2" ? 2 : 0 });
    this.onEvent("push");
    this.onEvent("roll");
  }

  private armBomb(x: number, y: number) {
    const s = this.state;
    if (s.bombs.some((b) => b.x === x && b.y === y)) return;
    s.bombs.push({ x, y, timer: 1000 });
    this.onEvent("fuse");
  }

  private updateBombs(dt: number) {
    const s = this.state;
    for (const b of [...s.bombs]) {
      b.timer -= dt;
      if (b.timer > 0) continue;
      s.bombs.splice(s.bombs.indexOf(b), 1);
      if (s.grid[b.y][b.x] === Cell.Crystal && s.kinds[b.y][b.x] === "bomb") { s.grid[b.y][b.x] = Cell.Floor; s.kinds[b.y][b.x] = null; }
      this.explode(b.x, b.y);
    }
  }

  private iceBurst(cx: number, cy: number) {
    const s = this.state;
    for (const e of s.enemies) {
      if (Math.abs(e.x - cx) <= 1 && Math.abs(e.y - cy) <= 1) {
        e.frozen = 5000;
        e.x = e.tx; e.y = e.ty; e.t = 1;
        if (e.kind === "bat" && s.grid[e.y][e.x] === Cell.Floor) { e.flying = false; e.flyTimer = 5500; }
      }
    }
    s.flashes.push({ x: cx + 0.5, y: cy + 0.5, life: 700, max: 700, color: "#7dd3fc", radius: 1.6 });
    this.burst(cx + 0.5, cy + 0.5, "#bae6fd", 30);
    s.shake = Math.max(s.shake, 5);
    this.onEvent("freezeburst");
    this.ensureCrystals();
  }

  private explode(cx: number, cy: number) {
    const s = this.state;
    const p = s.player;
    let killed = 0;
    for (let y = cy - 1; y <= cy + 1; y++) for (let x = cx - 1; x <= cx + 1; x++) {
      if (!inBounds(x, y, s.cols, s.rows)) continue;
      if (s.grid[y][x] === Cell.Crystal && s.kinds[y][x] !== "diamond" && s.kinds[y][x] !== "diamond2") {
        s.grid[y][x] = Cell.Floor; s.kinds[y][x] = null;
        this.burst(x + 0.5, y + 0.5, "#ffffff", 8);
      }
      for (const e of [...s.enemies]) {
        if ((e.x === x && e.y === y) || (e.tx === x && e.ty === y)) {
          s.enemies.splice(s.enemies.indexOf(e), 1);
          killed++;
          s.score += SCORE.explode;
          this.burst(x + 0.5, y + 0.5, "#fb923c", 14);
        }
      }
    }
    s.flashes.push({ x: cx + 0.5, y: cy + 0.5, life: 700, max: 700, color: "#fb923c", radius: 1.6 });
    this.burst(cx + 0.5, cy + 0.5, "#f97316", 40);
    this.burst(cx + 0.5, cy + 0.5, "#fde047", 20);
    s.popups.push({ x: cx + 0.5, y: cy + 0.5, text: killed ? `BUM x${killed}` : "BUM", life: 1100, max: 1100 });
    s.shake = 14;
    this.onEvent("explode");
    if (p.invuln <= 0 && s.power <= 0 && Math.abs(p.x - cx) <= 1 && Math.abs(p.y - cy) <= 1) this.die();
    this.ensureCrystals();
    if (killed) this.scheduleReinforcements();
    this.notify();
  }

  private usableBalls() {
    const s = this.state;
    let n = s.sliders.filter((sl) => sl.kind === "ball").length;
    for (let y = 0; y < s.rows; y++) for (let x = 0; x < s.cols; x++) {
      if (s.grid[y][x] !== Cell.Crystal || s.kinds[y][x] !== "ball" || this.isEdge(x, y)) continue;
      for (const [d, v] of Object.entries(DIRS) as [Dir, { dx: number; dy: number }][]) {
        if (this.isFree(x + v.dx, y + v.dy) && inBounds(x - v.dx, y - v.dy, s.cols, s.rows) && s.grid[y - v.dy][x - v.dx] !== Cell.Wall && s.grid[y - v.dy][x - v.dx] !== Cell.Crystal) { n++; void d; break; }
      }
    }
    return n;
  }

  private totalBalls() {
    const s = this.state;
    return s.sliders.filter((sl) => sl.kind === "ball").length + s.kinds.flat().filter((k) => k === "ball").length;
  }

  private ensureCrystals(dt = 0) {
    const s = this.state;
    if (s.bonus) return;
    const target = s.daily ? Math.max(s.ballFloor, 7 + s.wave) : 4;
    if (this.usableBalls() >= target || this.totalBalls() >= Math.max(target, s.ballCap)) { this.ballTimer = 0; return; }
    this.ballTimer += dt;
    if (this.ballTimer < (s.daily ? 600 : 1500)) return;
    this.ballTimer = 0;
    const spots: { x: number; y: number }[] = [];
    for (let y = 1; y < s.rows - 1; y++) for (let x = 1; x < s.cols - 1; x++) {
      const p = s.player;
      if (this.isFree(x, y) && !this.enemyAt(x, y) && Math.abs(x - p.x) + Math.abs(y - p.y) >= 2 && !s.powerups.some((u) => u.x === x && u.y === y) && !s.bubbles.some((b) => b.x === x && b.y === y)) spots.push({ x, y });
    }
    const sp = spots.length ? spots[Math.floor(Math.random() * spots.length)] : this.randomFreeSpot();
    if (!sp) return;
    s.grid[sp.y][sp.x] = Cell.Crystal;
    s.kinds[sp.y][sp.x] = "ball";
    this.burst(sp.x + 0.5, sp.y + 0.5, "#fde68a", 10);
    s.flashes.push({ x: sp.x + 0.5, y: sp.y + 0.5, life: 500, max: 500, color: "#ffffff", radius: 0.8 });
  }

  private updateSliders(dt: number) {
    const s = this.state;
    for (const sl of [...s.sliders]) {
      sl.t = Math.min(1, sl.t + dt / (sl.kind === "bounce1" || sl.kind === "bounce2" ? SLIDE_MS * 2 : SLIDE_MS));
      if (sl.t < 1) continue;
      sl.x = sl.tx; sl.y = sl.ty;
      this.collectAt(sl.x, sl.y);
      this.crushAt(sl.x, sl.y, sl);
      s.powerups = s.powerups.filter((pu) => pu.x !== sl.x || pu.y !== sl.y);
      s.bubbles = s.bubbles.filter((b) => b.x !== sl.x || b.y !== sl.y);
      const { dx, dy } = DIRS[sl.dir];
      const nx = sl.x + dx, ny = sl.y + dy;
      const p = s.player;
      const playerBlocks = (p.x === nx && p.y === ny) || (p.tx === nx && p.ty === ny);
      const isDiaSlider = sl.kind === "diamond" || sl.kind === "diamond2";
      const maxBounces = sl.kind === "bounce1" ? 1 : sl.kind === "bounce2" ? 2 : 0;
      const returning = maxBounces > 0 && sl.bounces < maxBounces;
      if (returning && playerBlocks && p.invuln <= 0 && s.power <= 0) {
        this.die();
        s.sliders.splice(s.sliders.indexOf(sl), 1);
        s.grid[sl.y][sl.x] = Cell.Crystal;
        s.kinds[sl.y][sl.x] = sl.kind;
        this.onEvent("thud");
        continue;
      }
      if (isDiaSlider && this.isDiamond(nx, ny)) {
        s.sliders.splice(s.sliders.indexOf(sl), 1);
        this.onEvent("thud");
        this.mergeDiamond(sl.kind, nx, ny);
        continue;
      }
      if (!this.isFree(nx, ny, sl) || playerBlocks) {
        if (sl.bounces > 0) {
          const back: Dir = sl.dir === "up" ? "down" : sl.dir === "down" ? "up" : sl.dir === "left" ? "right" : "left";
          const bx = sl.x + DIRS[back].dx, by = sl.y + DIRS[back].dy;
          if (this.isFree(bx, by, sl)) {
            sl.bounces--;
            sl.dir = back;
            sl.tx = bx; sl.ty = by; sl.t = 0;
            s.shake = Math.max(s.shake, 3);
            this.burst(sl.x + 0.5, sl.y + 0.5, sl.kind === "bounce1" ? "#6ee7b7" : "#d8b4fe", 8);
            this.onEvent("thud");
            this.onEvent("roll");
            continue;
          }
        }
        s.sliders.splice(s.sliders.indexOf(sl), 1);
        if (sl.kind === "ice") { this.onEvent("thud"); this.iceBurst(sl.x, sl.y); continue; }
        s.grid[sl.y][sl.x] = Cell.Crystal;
        s.kinds[sl.y][sl.x] = sl.kind;
        s.shake = Math.max(s.shake, 3);
        this.onEvent("thud");
        if (sl.kind === "bomb") this.armBomb(sl.x, sl.y);
        continue;
      }
      sl.tx = nx; sl.ty = ny; sl.t = 0;
    }
  }

  private crushAt(x: number, y: number, sl: Slider) {
    const s = this.state;
    const victims = s.enemies.filter((e) => !e.flying && ((e.x === x && e.y === y) || (e.tx === x && e.ty === y)));
    let mult = 1;
    for (const e of victims) {
      s.enemies.splice(s.enemies.indexOf(e), 1);
      sl.crushed++;
      mult = sl.crushed === 1 ? 1 : sl.crushed === 2 ? 2 : 4;
      s.score += SCORE[e.kind] * mult;
      if (s.bonus) { s.score += TUNNEL_CRUSH_BONUS; s.popups.push({ x: x + 0.5, y: y - 0.3, text: `+${TUNNEL_CRUSH_BONUS}`, life: 1100, max: 1100 }); }
      s.lastCombo = sl.crushed;
      this.burst(x + 0.5, y + 0.5, e.kind === "snake" ? "#4ade80" : e.kind === "bat" ? "#c084fc" : "#f87171", 16);
      s.shake = 6;
      this.onEvent("crush");
    }
    if (victims.length && sl.crushed >= 2) {
      s.popups.push({ x: x + 0.5, y: y + 0.5, text: `x${mult}`, life: 1300, max: 1300 });
      s.shake = 9;
      this.onEvent("combo");
    }
    if (victims.length) { this.scheduleReinforcements(); this.notify(); }
  }

  private computeBfs() {
    const s = this.state;
    const { cols } = s;
    this.bfs.fill(-1);
    const p = s.player;
    const q: number[] = [p.y * cols + p.x];
    this.bfs[q[0]] = 0;
    for (let i = 0; i < q.length; i++) {
      const k = q[i];
      const x = k % cols, y = Math.floor(k / cols);
      for (const d of Object.values(DIRS)) {
        const nx = x + d.dx, ny = y + d.dy;
        if (!this.isFree(nx, ny)) continue;
        const nk = ny * cols + nx;
        if (this.bfs[nk] === -1) { this.bfs[nk] = this.bfs[k] + 1; q.push(nk); }
      }
    }
  }

  private updateEnemies(dt: number) {
    const s = this.state;
    if (s.freeze > 0 || s.introHold > 0) return;
    const { cols } = s;
    let bfsDone = false;
    for (const e of s.enemies) {
      if (e.frozen > 0) {
        e.frozen -= dt;
        if (e.frozen <= 0) {
          this.burst(e.x + 0.5, e.y + 0.5, "#bae6fd", 14);
          this.burst(e.x + 0.5, e.y + 0.5, "#ffffff", 6);
          s.flashes.push({ x: e.x + 0.5, y: e.y + 0.5, life: 350, max: 350, color: "#bae6fd", radius: 0.7 });
          this.onEvent("crack");
        }
        continue;
      }
      if (e.kind === "bat") e.flyTimer -= dt;
      let carry = 0;
      if (e.t < 1) {
        const need = (1 - e.t) * e.speed;
        if (dt < need) { e.t += dt / e.speed; continue; }
        e.t = 1; e.x = e.tx; e.y = e.ty; e.wait = 0;
        carry = Math.min(dt - need, e.speed * 0.5);
      }
      if (e.kind === "bat") { this.updateBat(e, dt); continue; }
      e.wait -= dt;
      if (e.wait > 0) continue;
      const options = (Object.keys(DIRS) as Dir[]).filter((d) => {
        const nx = e.x + DIRS[d].dx, ny = e.y + DIRS[d].dy;
        return this.isFree(nx, ny) && !this.enemyAt(nx, ny, e);
      });
      if (!options.length) {
        e.stuck += dt;
        if (e.stuck > 1500 && !s.bonus) this.burrow(e);
        continue;
      }
      e.stuck = 0;
      let choice: Dir;
      if (e.kind === "bouncer") {
        const others = options.filter((d) => d !== e.dir);
        choice = options.includes(e.dir) ? e.dir : others.length ? others[Math.floor(Math.random() * others.length)] : options[0];
        if (choice !== e.dir) s.shake = Math.max(s.shake, 1.5);
        e.dir = choice;
        e.tx = e.x + DIRS[choice].dx; e.ty = e.y + DIRS[choice].dy; e.t = carry / e.speed;
        continue;
      }
      if (Math.random() < this.chaseOf(e.kind, this.speedLevel())) {
        if (!bfsDone) { this.computeBfs(); bfsDone = true; }
        choice = options.reduce((best, d) => {
          const dist = (dd: Dir) => { const v = this.bfs[(e.y + DIRS[dd].dy) * cols + e.x + DIRS[dd].dx]; return v < 0 ? 999 : v; };
          return dist(d) < dist(best) ? d : best;
        }, options[0]);
      } else if (options.includes(e.dir) && Math.random() < 0.8) {
        choice = e.dir;
      } else {
        choice = options[Math.floor(Math.random() * options.length)];
      }
      e.dir = choice;
      e.tx = e.x + DIRS[choice].dx; e.ty = e.y + DIRS[choice].dy; e.t = carry / e.speed;
    }
  }

  private updateBat(e: Enemy, dt: number) {
    const s = this.state;
    void dt;
    if (!e.flying) {
      if (e.flyTimer <= 0) { e.flying = true; e.flyTimer = 2500 + Math.random() * 3000; }
      return;
    }
    if (e.flyTimer <= 0 && s.grid[e.y][e.x] === Cell.Floor && !s.sliders.some((sl) => sl.tx === e.x && sl.ty === e.y)) {
      e.flying = false;
      e.flyTimer = 1800 + Math.random() * 1200;
      return;
    }
    const options = (Object.keys(DIRS) as Dir[]).filter((d) => {
      const nx = e.x + DIRS[d].dx, ny = e.y + DIRS[d].dy;
      return inBounds(nx, ny, s.cols, s.rows) && s.grid[ny][nx] !== Cell.Wall && !this.enemyAt(nx, ny, e);
    });
    if (!options.length) return;
    const p = s.player;
    let choice: Dir;
    if (Math.random() < 0.35) {
      const dist = (d: Dir) => Math.abs(e.x + DIRS[d].dx - p.x) + Math.abs(e.y + DIRS[d].dy - p.y);
      choice = options.reduce((b, d) => (dist(d) < dist(b) ? d : b), options[0]);
    } else if (options.includes(e.dir) && Math.random() < 0.6) {
      choice = e.dir;
    } else {
      choice = options[Math.floor(Math.random() * options.length)];
    }
    e.dir = choice;
    e.tx = e.x + DIRS[choice].dx; e.ty = e.y + DIRS[choice].dy; e.t = 0;
  }

  private burrow(e: Enemy) {
    const s = this.state;
    for (const d of Object.values(DIRS)) {
      const nx = e.x + d.dx, ny = e.y + d.dy;
      if (inBounds(nx, ny, s.cols, s.rows) && s.grid[ny][nx] === Cell.Crystal && s.kinds[ny][nx] === "ball") {
        s.grid[ny][nx] = Cell.Floor;
        s.kinds[ny][nx] = null;
        this.burst(nx + 0.5, ny + 0.5, "#a5f3fc", 10);
        e.stuck = 0;
        this.onEvent("shatter");
        this.ensureCrystals();
        return;
      }
    }
  }

  private checkPlayerHit() {
    const s = this.state;
    const p = s.player;
    const pp = lerpPos(p);
    if (s.power > 0) {
      for (const e of [...s.enemies]) {
        const ep = lerpPos(e);
        if (Math.abs(pp.x - ep.x) < 0.7 && Math.abs(pp.y - ep.y) < 0.7) {
          s.enemies.splice(s.enemies.indexOf(e), 1);
          s.score += SCORE.eat;
          this.burst(ep.x + 0.5, ep.y + 0.5, "#86efac", 16);
          s.popups.push({ x: ep.x + 0.5, y: ep.y + 0.5, text: t("yum"), life: 900, max: 900 });
          s.shake = 5;
          this.onEvent("eat");
          this.scheduleReinforcements();
          this.notify();
        }
      }
      return;
    }
    if (p.invuln > 0 || s.freeze > 0) return;
    for (const e of s.enemies) {
      if (e.frozen > 0) continue;
      const ep = lerpPos(e);
      if (Math.abs(pp.x - ep.x) < 0.6 && Math.abs(pp.y - ep.y) < 0.6) { this.die(); return; }
    }
  }

  private die() {
    const s = this.state;
    if (this.god) { s.player.invuln = Math.max(s.player.invuln, 800); return; }
    if (!s.bonus) s.lives--;
    s.player.deadTimer = DEATH_MS;
    s.shake = 8;
    this.burst(s.player.x + 0.5, s.player.y + 0.5, "#fca5a5", 18);
    this.swipe = null;
    this.onEvent("death");
    this.notify();
  }

  private afterDeath() {
    const s = this.state;
    if (s.bonus) {
      s.bonusResult = "fail";
      s.phase = "levelclear";
      s.player.deadTimer = 0;
      this.notify();
      return;
    }
    if (s.lives <= 0) {
      s.phase = "gameover";
      if (!s.daily) s.retryAt = applySetback(s.level);
      this.saveHigh();
      this.onEvent("gameover");
      this.notify();
      return;
    }
    let best = { x: s.player.x, y: s.player.y, d: -1 };
    for (let y = 0; y < s.rows; y++) for (let x = 0; x < s.cols; x++) {
      if (!this.isFree(x, y)) continue;
      const d = Math.min(...s.enemies.map((e) => Math.abs(e.x - x) + Math.abs(e.y - y)), 99);
      if (d > best.d) best = { x, y, d };
    }
    Object.assign(s.player, { x: best.x, y: best.y, tx: best.x, ty: best.y, t: 1, invuln: 2000, deadTimer: 0 });
  }

  private saveHigh() {
    if (this.state.daily) return;
    if (this.state.score > this.best) { this.best = this.state.score; saveBest(this.best); }
  }

  private burst(x: number, y: number, color: string, n: number) {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 2 + Math.random() * 5;
      this.state.particles.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 500 + Math.random() * 300, max: 800, color, size: 0.06 + Math.random() * 0.1 });
    }
  }

  private updateParticles(dt: number) {
    const ps = this.state.particles;
    for (let i = ps.length - 1; i >= 0; i--) {
      const p: Particle = ps[i];
      p.life -= dt;
      if (p.life <= 0) { ps.splice(i, 1); continue; }
      p.x += p.vx * dt / 1000; p.y += p.vy * dt / 1000;
      p.vx *= 0.96; p.vy = p.vy * 0.96 + 0.15;
    }
  }
}
